import React from "react";
import { Box, Grid, useMediaQuery, useTheme } from "@mui/material";
import { Base } from "../../../base";
import Attachment from "../../../../types/attachment";
import ColumnDef from "../../../../types/columnDef";
import { useSelector } from "../../../../store";
import moment from "moment";
import { GridCellParams } from "@mui/x-data-grid-premium";

interface Props {
  handleDownload: (id: number) => void;
  title: string;
  attachments: Attachment[];
  grid?: number;
}

const JobViewAttachments: React.FC<Props> = (props) => {
  const { handleDownload, title, attachments, grid } = props;
  const theme = useTheme();
  const { simpleUserList } = useSelector((store) => store.user);
  const { attachmentTypes } = useSelector((store) => store.attachmentType);
  const xs = useMediaQuery(theme.breakpoints.down("sm"));

  const columns: ColumnDef[] = [
    {
      field: "name",
      headerName: "Name",
      align: "left",
      headerAlign: "center",
      flex: 1,
      headerClassName: "header",
      mobileSize: 12,
      filterable: false,
    },
    {
      field: "note",
      headerName: "Note",
      align: "left",
      headerAlign: "center",
      flex: 1,
      headerClassName: "header",
      mobileSize: 12,
      filterable: false,
    },
    {
      field: "originalJob",
      headerName: "Uploaded In",
      align: "left",
      flex: 1,
      headerClassName: "header",
      sortable: false,
      disableColumnMenu: true,
      disableExport: true,
      filterable: false,
      mobileSize: 12,
    },
    {
      field: "type",
      headerName: "Attachment Type",
      align: "left",
      headerAlign: "center",
      flex: 0.5,
      headerClassName: "header",
      mobileSize: 12,
      type: "singleSelect",
      valueOptions: attachmentTypes.map((type) => {
        return { value: type.id, label: type.name };
      }),
      renderCell: ({ value }) => <>{value.label}</>,
    },
    {
      field: "createdBy",
      headerName: "Uploaded By",
      align: "left",
      headerAlign: "center",
      flex: 0.5,
      headerClassName: "header",
      mobileSize: 12,
      filterable: false,
    },
    {
      field: "uploadedOn",
      headerName: "Uploaded On",
      align: "left",
      headerAlign: "center",
      flex: 0.5,
      headerClassName: "header",
      mobileSize: 12,
      filterable: false,
    },
    {
      field: "actions",
      headerName: "Actions",
      headerAlign: "center",
      align: "center",
      flex: 0.5,
      headerClassName: "header",
      sortable: false,
      disableColumnMenu: true,
      disableExport: true,
      filterable: false,
      renderCell: (params) => <>{params.value}</>,
      mobileSize: 12,
    },
  ];

  const displayRows = (array: any[]) => {
    let jobAttachments: {
      id: number | undefined;
      name: string | undefined;
      note: string;
      originalJob: string | undefined;
      originalJobId: number | undefined;
      mobileTitle: string | undefined;
      type: string | { value: number | undefined; label: string | undefined };
      createdBy: string | undefined;
      uploadedOn: string | undefined;
      actions: JSX.Element;
    }[] = [];
    array.forEach((attachment: Attachment) => {
      const user = simpleUserList.find((u) => u.id === attachment.createdById);
      jobAttachments.push({
        id: attachment.id,
        mobileTitle:
          attachment?.name?.length! > 40
            ? `${attachment.name?.slice(0, 40)}...${
                attachment.name?.split(".")[1]
              } `
            : attachment?.name,
        name:
          attachment?.name?.length! > 40
            ? `${attachment.name?.slice(0, 40)}...${
                attachment.name?.split(".")[1]
              } `
            : attachment?.name,
        note: !!attachment.note ? attachment.note : "N/A",
        originalJob: attachment.originalJobTitle ?? "N/A",
        originalJobId: attachment.originalJobId ?? undefined,
        type: xs
          ? attachment.attachmentType?.name ?? "N/A"
          : {
              value: attachment.attachmentType?.id ?? undefined,
              label: attachment.attachmentType?.name ?? "N/A",
            },
        createdBy: `${user?.value}`,
        uploadedOn: moment(attachment.uploadedDate).format("MM/DD/YYYY"),
        actions: (
          <Base.TableActions
            id={attachment.id!}
            handleView={
              attachment.viewable
                ? () =>
                    window.open(attachment.url, "_blank", "noopener,noreferrer")
                : undefined
            }
            handleDownload={handleDownload}
          />
        ),
      });
    });
    return jobAttachments;
  };

  const onCellClick = (params: any) => {
    if (params.field === "originalJob") {
      if (params.row.originalJobId > 0) {
        window.open(
          `/job/view/${params.row.originalJobId}`,
          "_blank",
          "noopener,noreferrer",
        );
      }
    }
  };

  return (
    <Grid size={{ xs: grid ?? 12 }}>
      {!!attachments.length && (
        <>
          <Box sx={{ fontWeight: "bold" }} component={"span"}>
            <Base.Text text={title} variant="h4" />
          </Box>
          <Box
            sx={{
              "& .header": {
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
              },
              "& .link": {
                cursor: "pointer",
              },
            }}
          >
            <Base.TableContainer
              columns={columns}
              rows={displayRows(attachments)}
              mobileRows={displayRows(attachments)}
              pageSize={5}
              totalRows={attachments.length}
              onCellClick={onCellClick}
              getCellClassName={(params: GridCellParams<any, any, any>) => {
                return params.field === "originalJob" &&
                  params.row.originalJobId > 0
                  ? "link"
                  : "";
              }}
            />
          </Box>
        </>
      )}
    </Grid>
  );
};

export default JobViewAttachments;
